// colombiaData & Engine constants
import { SMLMV_HISTORIAL, IPC_HISTORIAL } from "./colombiaData";

export interface BreakdownItem {
  label: string;
  value: string | number;
}

export interface CalculationResult {
  title: string;
  total: number;
  summary: string;
  breakdown: BreakdownItem[];
  normativa: string[];
}

// 1. Sentencias Administrativas (CPACA Art. 192/195)
export function calcularSentenciaAdministrativa(
  capitalHistorico: number,
  mesesTranscurridos: number,
  aplicaPlazoGracia: boolean = true
): CalculationResult {
  const ipcPromedioAnual = 0.055;
  const factorIndexacion = Math.pow(1 + ipcPromedioAnual, mesesTranscurridos / 12);
  const capitalIndexado = capitalHistorico * factorIndexacion;

  const mesesEfectivosMora = aplicaPlazoGracia ? Math.max(0, mesesTranscurridos - 10) : mesesTranscurridos;
  const tasaMoraAnual = 0.12;
  const interesesMora = capitalIndexado * (tasaMoraAnual * (mesesEfectivosMora / 12));

  const total = capitalIndexado + interesesMora;

  return {
    title: "Liquidación de Sentencia Administrativa (CPACA)",
    total: Math.round(total),
    summary: `Capital histórico indexado por IPC más intereses de mora (${mesesEfectivosMora} meses contados tras 10 meses de plazo de gracia Art. 192).`,
    breakdown: [
      { label: "Capital Histórico", value: capitalHistorico },
      { label: "Factor de Indexación IPC Acumulado", value: `${((factorIndexacion - 1) * 100).toFixed(2)}%` },
      { label: "Capital Actualizado (Indexado)", value: Math.round(capitalIndexado) },
      { label: "Meses de Plazo de Gracia Aplicados", value: aplicaPlazoGracia ? "10 Meses (Sin mora)" : "0 Meses" },
      { label: "Meses con Intereses de Mora", value: `${mesesEfectivosMora} Meses` },
      { label: "Intereses Moratorios Acumulados", value: Math.round(interesesMora) }
    ],
    normativa: [
      "Ley 1437 de 2011 (CPACA) Artículos 192 y 195",
      "Sentencia de Unificación CE Sección Tercera sobre actualización monetaria e intereses simples sin anatocismo"
    ]
  };
}

// 2. Privación Injusta de la Libertad
export function calcularPrivacionInjusta(
  diasPrivacion: number,
  salarioMensualVictima: number,
  familiares: { cónyuge: boolean; padres: number; hijos: number }
): CalculationResult {
  const smlmv2026 = 1550000;
  const meses = diasPrivacion / 30;
  
  let smlmvMoralVictima = 15;
  if (meses > 18) smlmvMoralVictima = 100;
  else if (meses > 12) smlmvMoralVictima = 70;
  else if (meses > 6) smlmvMoralVictima = 50;
  else if (meses > 3) smlmvMoralVictima = 35;

  const danoMoralVictima = smlmvMoralVictima * smlmv2026;
  const totalFamiliaresDirectos = (familiares.cónyuge ? 1 : 0) + familiares.padres + familiares.hijos;
  const danoMoralFamiliares = totalFamiliaresDirectos * (danoMoralVictima * 0.8);
  const lucroCesante = (salarioMensualVictima * 1.25) * meses;

  const total = danoMoralVictima + danoMoralFamiliares + lucroCesante;

  return {
    title: "Indemnización por Privación Injusta de la Libertad",
    total: Math.round(total),
    summary: `Indemnización por ${Math.round(diasPrivacion)} días de detención preventiva injusta segun jurisprudencia del Consejo de Estado.`,
    breakdown: [
      { label: "Días Totales de Detención", value: Math.round(diasPrivacion) },
      { label: "Daño Moral Víctima Directa", value: `${smlmvMoralVictima} SMLMV ($${danoMoralVictima.toLocaleString("es-CO")})` },
      { label: "Familiares 1er Grado Certificados", value: totalFamiliaresDirectos },
      { label: "Daño Moral Grupo Familiar", value: Math.round(danoMoralFamiliares) },
      { label: "Lucro Cesante (Salario + Prestaciones)", value: Math.round(lucroCesante) }
    ],
    normativa: [
      "Ley 270 de 1996 Artículo 65",
      "Consejo de Estado, Sección Tercera, Sentencia de Unificación Rad. 36009 (Escala fijación perjuicios morales)"
    ]
  };
}

// 3. Mora en Contratos Estatales
export function calcularMoraContratosEstatales(
  valorObligacion: number,
  diasMora: number
): CalculationResult {
  const tasaMoraMensual = 0.01;
  const factorIPC = 1 + (0.05 * (diasMora / 365));
  const valorIndexado = valorObligacion * factorIPC;
  const intereses = valorIndexado * (tasaMoraMensual * (diasMora / 30));

  return {
    title: "Liquidación Mora Contrato Estatal (Ley 80 / Dec 679)",
    total: Math.round(valorIndexado + intereses),
    summary: `Actualización monetaria por IPC + 1% mensual moratorio según Ley 80 de 1993.`,
    breakdown: [
      { label: "Valor Histórico de la Obligación", value: valorObligacion },
      { label: "Valor Capital Indexado (IPC)", value: Math.round(valorIndexado) },
      { label: "Días Transcurridos en Mora", value: diasMora },
      { label: "Intereses Moratorios (1% Mensual)", value: Math.round(intereses) }
    ],
    normativa: [
      "Ley 80 de 1993 Art. 4 Num. 8",
      "Decreto 679 de 1994"
    ]
  };
}

// 4. Competencia y Cuantía
export function calcularCompetenciaCuantia(
  pretensiones: number,
  materia: "civil" | "laboral" | "familia" | "administrativo"
): CalculationResult {
  const smlmv = 1550000;
  const minimaLimite = 40 * smlmv;
  const menorLimite = 150 * smlmv;

  let tipoCuantia = "Mayor Cuantía";
  let juezCompetente = "Juez del Circuito / Tribunal";

  if (pretensiones <= minimaLimite) {
    tipoCuantia = "Mínima Cuantía (≤ 40 SMLMV)";
    juezCompetente = "Juez Municipal en Única Instancia";
  } else if (pretensiones <= menorLimite) {
    tipoCuantia = "Menor Cuantía (> 40 y ≤ 150 SMLMV)";
    juezCompetente = "Juez Municipal en Primera Instancia";
  } else {
    tipoCuantia = "Mayor Cuantía (> 150 SMLMV)";
    juezCompetente = "Juez del Circuito en Primera Instancia";
  }

  return {
    title: "Determinación de Competencia por Cuantía",
    total: pretensiones,
    summary: `Para pretensiones de $${pretensiones.toLocaleString("es-CO")} COP en materia ${materia.toUpperCase()}.`,
    breakdown: [
      { label: "Monto Pretensiones Ingresado", value: pretensiones },
      { label: "Valor 1 SMLMV (2026)", value: smlmv },
      { label: "Tope Mínima Cuantía (40 SMLMV)", value: minimaLimite },
      { label: "Tope Menor Cuantía (150 SMLMV)", value: menorLimite },
      { label: "Tipo de Cuantía Determinado", value: tipoCuantia },
      { label: "Juez Competente", value: juezCompetente }
    ],
    normativa: [
      "Código General del Proceso (CGP) Artículos 25 y 26",
      "Código Procesal del Trabajo y CPACA sobre competencia cuantificable"
    ]
  };
}

// 5. Cómputo de Plazos Procesales
export function calcularPlazoProcesal(
  fechaInicio: string,
  diasHabiles: number,
  notificacion: "personal" | "estado" | "aviso" | "estrados"
): CalculationResult {
  const start = new Date(fechaInicio);
  let current = new Date(start);
  
  if (notificacion === "estado") {
    current.setDate(current.getDate() + 1);
  } else if (notificacion === "aviso") {
    current.setDate(current.getDate() + 3);
  }

  let added = 0;
  while (added < diasHabiles) {
    current.setDate(current.getDate() + 1);
    const day = current.getDay();
    if (day !== 0 && day !== 6) {
      added++;
    }
  }

  const vencimientoStr = current.toISOString().split("T")[0];

  return {
    title: "Cómputo de Plazo Procesal Hábil",
    total: 0,
    summary: `Término de ${diasHabiles} días hábiles contados tras notificación por ${notificacion.toUpperCase()}.`,
    breakdown: [
      { label: "Fecha de Providencia / Notificación", value: fechaInicio },
      { label: "Mecanismo de Notificación", value: notificacion.toUpperCase() },
      { label: "Días Hábiles a Computar", value: diasHabiles },
      { label: "Fecha de Vencimiento del Término", value: vencimientoStr }
    ],
    normativa: [
      "Código General del Proceso (CGP) Artículo 118",
      "Ley 2213 de 2022 sobre notificaciones por medios electrónicos"
    ]
  };
}

// 6. Indexación IPC (Con trazabilidad, factor explícito y DANE Base 2018=100)
export interface IndexacionIPCParams {
  valorHistorico: number;
  fechaOrigen?: string;
  fechaCorte?: string;
  ipcInicialCustom?: number;
  ipcFinalCustom?: number;
}

export function calcularIndexacionIPC(
  valorHistorico: number,
  ipcInicial: number = 106.1,
  ipcFinal: number = 156.4,
  options: Partial<IndexacionIPCParams> = {}
): CalculationResult {
  const fOrig = options.fechaOrigen || "2021-01";
  const fCorte = options.fechaCorte || "2026-08";

  const i1 = (options.ipcInicialCustom && options.ipcInicialCustom > 0) ? options.ipcInicialCustom : ipcInicial;
  const i2 = (options.ipcFinalCustom && options.ipcFinalCustom > 0) ? options.ipcFinalCustom : ipcFinal;

  const factor = i2 / i1;
  const valorPresente = valorHistorico * factor;
  const incremento = valorPresente - valorHistorico;
  const variacionPct = (factor - 1) * 100;

  return {
    title: "VALOR ACTUALIZADO A LA FECHA DE CORTE",
    total: Math.round(valorPresente),
    summary: `Actualización monetaria de $${valorHistorico.toLocaleString("es-CO")} conforme al IPC DANE (Base 2018=100) desde ${fOrig} hasta ${fCorte}.`,
    breakdown: [
      { label: "Valor Histórico de Origen", value: valorHistorico },
      { label: `Fecha de Origen (${fOrig})`, value: fOrig },
      { label: "Índice IPC Inicial DANE", value: i1 },
      { label: `Fecha de Corte (${fCorte})`, value: fCorte },
      { label: "Índice IPC Final DANE", value: i2 },
      { label: "Base Estadística Oficial", value: "DANE Base 2018=100" },
      { label: "Factor de Actualización (IPC_final / IPC_inicial)", value: factor.toFixed(6) },
      { label: "Variación Inflacionaria Acumulada", value: `${variacionPct.toFixed(2)}%` },
      { label: "Incremento por Actualización Monetaria", value: Math.round(incremento) },
      { label: "VALOR ACTUALIZADO A LA FECHA DE CORTE", value: Math.round(valorPresente) }
    ],
    normativa: [
      "Departamento Administrativo Nacional de Estadística - DANE: Índice de Precios al Consumidor (IPC) Base 2018=100",
      "Corte Suprema de Justicia - Sala de Casación Civil: Sentencia SC-3201 de 2021 (Fórmula Vp = Vh * [IPC_final / IPC_inicial])",
      "Consejo de Estado - Sección Tercera: Unificación de Criterios sobre Actualización Monetaria sin Anatocismo",
      "ADVERTENCIA LEGAL: La indexación restituye el poder adquisitivo frente a la inflación y NO constituye ni sustituye el cobro de intereses moratorios ni remuneratorios."
    ]
  };
}

  // 7. Liquidación Cuota Alimentaria
export function calcularCuotaAlimentaria(
  mesesDeuda: number,
  cuotaMensual: number
): CalculationResult {
  const capital = cuotaMensual * mesesDeuda;
  const intereses = capital * (0.01 * mesesDeuda);
  const total = capital + intereses;

  return {
    title: "Liquidación de Cuota Alimentaria Vencida",
    total: Math.round(total),
    summary: `Cómputo de ${mesesDeuda} meses de cuotas alimentarias en mora más intereses.`,
    breakdown: [
      { label: "Cuota Mensual Pactada / Decretada", value: cuotaMensual },
      { label: "Meses Acumulados en Mora", value: mesesDeuda },
      { label: "Capital Adeudado", value: capital },
      { label: "Intereses Moratorios Estimados", value: Math.round(intereses) }
    ],
    normativa: [
      "Ley 1098 de 2006 (Código Infancia y Adolescencia) Art. 129",
      "Código Civil Colombiano Art. 1617"
    ]
  };
}

// 8. Cánones de Arrendamiento
export function calcularCanonesArrendamiento(
  mesesDeuda: number,
  canonMensual: number
): CalculationResult {
  const capital = canonMensual * mesesDeuda;
  const intereses = capital * (0.015 * mesesDeuda);
  const total = capital + intereses;

  return {
    title: "Liquidación Cánones de Arrendamiento en Mora",
    total: Math.round(total),
    summary: `Cómputo de ${mesesDeuda} cánones mensuales impagos con moratorios.`,
    breakdown: [
      { label: "Canon Mensual", value: canonMensual },
      { label: "Meses de Arrendamiento Adeudados", value: mesesDeuda },
      { label: "Total Cánones Insolutos", value: capital },
      { label: "Intereses de Mora Comerciales", value: Math.round(intereses) }
    ],
    normativa: [
      "Ley 820 de 2003 (Arrendamiento de Vivienda Urbana)",
      "Código de Comercio Art. 884"
    ]
  };
}

// 9. Mora Propiedad Horizontal
export function calcularMoraPH(
  mesesDeuda: number,
  cuotaAdministracion: number
): CalculationResult {
  const capital = cuotaAdministracion * mesesDeuda;
  const intereses = capital * (0.02 * mesesDeuda);
  const total = capital + intereses;

  return {
    title: "Liquidación Mora Propiedad Horizontal (Ley 675/2001)",
    total: Math.round(total),
    summary: `Cuotas de administración impagas con interés del 1.5x Tasa Bancaria Corriente.`,
    breakdown: [
      { label: "Cuota Mensual de Administración", value: cuotaAdministracion },
      { label: "Meses en Mora", value: mesesDeuda },
      { label: "Capital Adeudado PH", value: capital },
      { label: "Intereses Moratorios (1.5x IBC)", value: Math.round(intereses) }
    ],
    normativa: [
      "Ley 675 de 2001 Artículo 30",
      "Certificación de Representación Legal y Mérito Ejecutivo Art. 48"
    ]
  };
}

// 10. Liquidación Laboral
// 10. Liquidación Laboral Completa (Con desglosado por anualidades, semestres y saldos pendientes CST)
export interface LiquidacionLaboralParams {
  salarioMensual: number;
  diasTrabajados?: number;
  fechaInicio?: string; // YYYY-MM-DD
  fechaTerminacion?: string; // YYYY-MM-DD
  incluyeAuxilio?: boolean;
  tipoContrato?: "indefinido" | "fijo" | "obra";
  causaTerminacion?: "despido_sin_justa" | "renuncia" | "despido_justa" | "mutuo_acuerdo" | "terminacion_obra";
  cesantiasConsignadas?: number;
  interesesPagados?: number;
  primasPagadas?: number;
  diasVacacionesDisfrutados?: number;
  abonosRealizados?: number;
  diasMoraArt65?: number;
  salariosPendientes?: number;
}

export function calcularLiquidacionLaboral(
  salarioMensual: number,
  diasTrabajados: number = 360,
  incluyeAuxilio: boolean = true,
  tipoContrato: "indefinido" | "fijo" | "obra" = "indefinido",
  options: Partial<LiquidacionLaboralParams> = {}
): CalculationResult {
  const causa = options.causaTerminacion || "despido_sin_justa";
  const fechaIniStr = options.fechaInicio || "2024-01-01";
  const fechaFinStr = options.fechaTerminacion || "2024-12-31";
  const cesantiasConsignadas = options.cesantiasConsignadas || 0;
  const interesesPagados = options.interesesPagados || 0;
  const primasPagadas = options.primasPagadas || 0;
  const diasVacDisfrutados = options.diasVacacionesDisfrutados || 0;
  const abonos = options.abonosRealizados || 0;
  const salariosPend = options.salariosPendientes || 0;
  const diasMora = options.diasMoraArt65 || 0;

  const d1 = new Date(fechaIniStr);
  const d2 = new Date(fechaFinStr);
  
  let totalDiasCalculados = diasTrabajados;
  if (options.fechaInicio && options.fechaTerminacion && !isNaN(d1.getTime()) && !isNaN(d2.getTime())) {
    const startYr = d1.getFullYear();
    const endYr = d2.getFullYear();
    const startMo = d1.getMonth();
    const endMo = d2.getMonth();
    const startDy = Math.min(30, d1.getDate());
    const endDy = Math.min(30, d2.getDate());
    
    totalDiasCalculados = Math.max(1, (endYr - startYr) * 360 + (endMo - startMo) * 30 + (endDy - startDy + 1));
  }

  // 1. Auxilio de Transporte
  const smlmv2026 = 1550000;
  const auxTransporte2026 = 200000;
  const auxTransporte = (incluyeAuxilio && salarioMensual <= 2 * smlmv2026) ? auxTransporte2026 : 0;
  const basePrestaciones = salarioMensual + auxTransporte;

  // 2. Anualidades de Cesantías e Intereses (Ley 52/1975)
  const startYr = d1.getFullYear() || 2024;
  const endYr = d2.getFullYear() || 2026;
  
  let cesantiasTotalesCausadas = 0;
  let interesesTotalesCausados = 0;
  let detalleAnualidades: string[] = [];

  for (let yr = startYr; yr <= endYr; yr++) {
    const dtStartYr = new Date(yr, 0, 1) > d1 ? new Date(yr, 0, 1) : d1;
    const dtEndYr = new Date(yr, 11, 31) < d2 ? new Date(yr, 11, 31) : d2;
    
    const y1 = dtStartYr.getFullYear();
    const y2 = dtEndYr.getFullYear();
    const m1 = dtStartYr.getMonth();
    const m2 = dtEndYr.getMonth();
    const dy1 = Math.min(30, dtStartYr.getDate());
    const dy2 = Math.min(30, dtEndYr.getDate());
    
    const diasYr = Math.max(1, (y2 - y1) * 360 + (m2 - m1) * 30 + (dy2 - dy1 + 1));
    const cesYr = (basePrestaciones * diasYr) / 360;
    const intYr = (cesYr * diasYr * 0.12) / 360;

    cesantiasTotalesCausadas += cesYr;
    interesesTotalesCausados += intYr;
    detalleAnualidades.push(`Año ${yr}: ${diasYr} días -> Cesantías: $${Math.round(cesYr).toLocaleString("es-CO")}, Intereses (12%): $${Math.round(intYr).toLocaleString("es-CO")}`);
  }

  const cesantiasPendientes = Math.max(0, cesantiasTotalesCausadas - cesantiasConsignadas);
  const interesesPendientes = Math.max(0, interesesTotalesCausados - interesesPagados);

  // 3. Prima de Servicios (Semestres)
  const primaTotalCausada = (basePrestaciones * totalDiasCalculados) / 360;
  const primaPendiente = Math.max(0, primaTotalCausada - primasPagadas);

  // 4. Vacaciones (Art. 186 CST - Excluye Auxilio de Transporte)
  const vacacionesCausadas = (salarioMensual * totalDiasCalculados) / 720;
  const valorVacacionesDisfrutadas = (salarioMensual / 30) * diasVacDisfrutados;
  const vacacionesPendientes = Math.max(0, vacacionesCausadas - valorVacacionesDisfrutadas);

  // 5. Indemnización por Despido sin Justa Causa (Art. 64 CST)
  let indemnizacion = 0;
  let justificacionIndemnizacion = "No aplica indemnización por la causal de terminación elegida.";

  if (causa === "despido_sin_justa") {
    if (tipoContrato === "indefinido") {
      if (salarioMensual < 10 * smlmv2026) {
        if (totalDiasCalculados <= 360) {
          indemnizacion = salarioMensual;
          justificacionIndemnizacion = "Contrato Indefinido (Salario < 10 SMLMV): 30 días de salario primer año o fracción.";
        } else {
          indemnizacion = salarioMensual + (salarioMensual * (20 / 30) * ((totalDiasCalculados - 360) / 360));
          justificacionIndemnizacion = `Contrato Indefinido (Salario < 10 SMLMV): 30 días primer año + 20 días/año por subsiguientes (${totalDiasCalculados - 360} días).`;
        }
      } else {
        if (totalDiasCalculados <= 360) {
          indemnizacion = (salarioMensual / 30) * 20;
          justificacionIndemnizacion = "Contrato Indefinido (Salario >= 10 SMLMV): 20 días de salario primer año.";
        } else {
          indemnizacion = ((salarioMensual / 30) * 20) + (salarioMensual * (15 / 30) * ((totalDiasCalculados - 360) / 360));
          justificacionIndemnizacion = "Contrato Indefinido (Salario >= 10 SMLMV): 20 días primer año + 15 días/año subsiguientes.";
        }
      }
    } else {
      indemnizacion = (salarioMensual / 30) * Math.max(15, 30);
      justificacionIndemnizacion = "Contrato Término Fijo/Obra: Salarios del tiempo faltante (mínimo 15 días).";
    }
  }

  // 6. Posible Sanción Moratoria (Art. 65 CST)
  const valorMoratoriaEstimada = diasMora > 0 ? (salarioMensual / 30) * diasMora : 0;

  // Total Neto Adeudado
  const totalPrestaciones = cesantiasPendientes + interesesPendientes + primaPendiente;
  const subtotalBruto = salariosPend + totalPrestaciones + vacacionesPendientes + indemnizacion;
  const totalNetoAdeudado = Math.max(0, subtotalBruto - abonos);

  return {
    title: "Liquidación Jurídica de Prestaciones e Indemnización Laboral (CST)",
    total: Math.round(totalNetoAdeudado),
    summary: `Liquidación desglosada por anualidades (${totalDiasCalculados} días laborados). Causal: ${causa.toUpperCase().replace(/_/g, " ")}.`,
    breakdown: [
      { label: "SECCIÓN A: Salarios Pendientes en Mora", value: Math.round(salariosPend) },
      { label: "Salario Base Ordinario Mensual", value: salarioMensual },
      { label: "Auxilio de Transporte (Si aplica <= 2 SMLMV)", value: auxTransporte },
      { label: "SECCIÓN B: Cesantías Pendientes (Causadas menos consignadas)", value: Math.round(cesantiasPendientes) },
      { label: "Intereses a las Cesantías Pendientes (12% por anualidad)", value: Math.round(interesesPendientes) },
      { label: "Prima de Servicios Pendiente", value: Math.round(primaPendiente) },
      { label: "SECCIÓN C: Vacaciones Pendientes (Excluye aux. transporte)", value: Math.round(vacacionesPendientes) },
      { label: "SECCIÓN D: Indemnización por Despido Sin Justa Causa (Art. 64 CST)", value: Math.round(indemnizacion) },
      { label: "Detalle Indemnización", value: justificacionIndemnizacion },
      { label: "SECCIÓN E: Menos Abonos y Pagos Previos Realizados", value: -Math.round(abonos) },
      { label: "MÓDULO INFORMATIVO: Posible Sanción Moratoria (Art. 65 CST)", value: Math.round(valorMoratoriaEstimada) }
    ],
    normativa: [
      "Código Sustantivo del Trabajo (CST) Artículos 64, 65, 186, 249, 306",
      "Ley 52 de 1975 y Decreto 116 de 1976 (Intereses a las Cesantías del 12% por anualidad)",
      "Ley 15 de 1959 (Inclusión de auxilio de transporte en Cesantías/Prima, exclusión en Vacaciones)",
      "Corte Suprema de Justicia - Sala de Casación Laboral (Jurisprudencia de liquidación cronológica)"
    ]
  };
}

  export function calcularPensionLey797(
  ibl: number,
  semanas: number,
  edad: number,
  genero: "masculino" | "femenino"
): CalculationResult {
  const smlmv = 1550000;
  const sMin = 1300;
  const eMin = genero === "masculino" ? 62 : 57;

  if (semanas < sMin || edad < eMin) {
    return {
      title: "Pensión de Vejez Ley 797 (Requisitos no Cumplidos)",
      total: 0,
      summary: `No cumple requisitos mínimos (${eMin} años edad / 1.300 semanas).`,
      breakdown: [
        { label: "Edad del Afiliado", value: `${edad} Años (Requerido: ${eMin})` },
        { label: "Semanas Cotizadas", value: `${semanas} Semanas (Requerido: ${sMin})` },
        { label: "Estado del Derecho", value: "Pendiente por cumplir edad o semanas" }
      ],
      normativa: ["Ley 797 de 2003 Artículo 9"]
    };
  }

  const sExtra = Math.floor((semanas - sMin) / 50);
  let tasaReemplazo = 65.5 - 0.5 * (ibl / smlmv) + (sExtra * 1.5);
  if (tasaReemplazo > 80) tasaReemplazo = 80;
  if (tasaReemplazo < 50) tasaReemplazo = 50;

  const mesada = ibl * (tasaReemplazo / 100);

  return {
    title: "Cálculo Pensión de Vejez Ley 797",
    total: Math.round(mesada),
    summary: `Cumple requisitos. Tasa de reemplazo estimada del ${tasaReemplazo.toFixed(2)}%.`,
    breakdown: [
      { label: "Ingreso Base de Liquidación (IBL)", value: ibl },
      { label: "Semanas Totales Acreditadas", value: semanas },
      { label: "Semanas Adicionales sobre 1.300", value: semanas - sMin },
      { label: "Tasa de Reemplazo Calculada", value: `${tasaReemplazo.toFixed(2)}%` },
      { label: "Monto Mesada Pensional Estimada", value: Math.round(mesada) }
    ],
    normativa: [
      "Ley 797 de 2003 Art. 9",
      "Ley 100 de 1993 Art. 34"
    ]
  };
}

// 12. Pensión de Invalidez (Ley 860/2003 & Ley 776/2002)
export function calcularPensionInvalidez(
  ibl: number,
  pcl: number,
  semanasUltimos3Anios: number,
  origen: "comun" | "laboral" = "comun",
  granInvalidez: boolean = false
): CalculationResult {
  const smlmv2026 = 1550000;

  if (pcl < 50) {
    return {
      title: "Pensión de Invalidez (PCL Inferior al 50%)",
      total: 0,
      summary: `La PCL dictaminada (${pcl}%) es inferior al 50% mínimo legal exigido por el Art. 38 Ley 100.`,
      breakdown: [
        { label: "Porcentaje PCL Registrado", value: `${pcl}% (Requerido: ≥ 50%)` },
        { label: "Origen del Siniestro", value: origen === "comun" ? "Origen Común" : "Origen Laboral (ARL)" },
        { label: "Dictamen Legal", value: "No causa pensión de invalidez. Posible indemnización o incapacidad permanente parcial." }
      ],
      normativa: ["Ley 100 de 1993 Art. 38 y 39", "Ley 860 de 2003"]
    };
  }

  let tasaReemplazo = 0;
  let reglaAplicada = "";

  if (origen === "comun") {
    if (pcl >= 50 && pcl < 66) {
      tasaReemplazo = 45 + (Math.max(0, semanasUltimos3Anios - 500) / 50) * 1.5;
      if (tasaReemplazo > 75) tasaReemplazo = 75;
      reglaAplicada = "Art. 40 Ley 100/1993 (PCL 50% - 65.9%: 45% base + 1.5% c/50 sem sobre 500)";
    } else {
      tasaReemplazo = 54 + (Math.max(0, semanasUltimos3Anios - 800) / 50) * 2.0;
      if (tasaReemplazo > 75) tasaReemplazo = 75;
      reglaAplicada = "Art. 40 Ley 100/1993 (PCL ≥ 66%: 54% base + 2.0% c/50 sem sobre 800)";
    }
  } else {
    // Origen Laboral (Ley 776 de 2002 ARL)
    if (pcl >= 50 && pcl < 66) {
      tasaReemplazo = 60;
      reglaAplicada = "Art. 10 Ley 776/2002 ARL (PCL 50% - 65.9%: 60% del IBL)";
    } else {
      tasaReemplazo = 75;
      reglaAplicada = "Art. 10 Ley 776/2002 ARL (PCL ≥ 66%: 75% del IBL)";
    }

    if (granInvalidez) {
      tasaReemplazo += 15;
      reglaAplicada += " + 15% Incremento por Gran Invalidez (Necesidad de auxilio de terceros)";
    }
  }

  let mesada = ibl * (tasaReemplazo / 100);
  if (mesada < smlmv2026) mesada = smlmv2026; // Garantía de Pensión Mínima

  return {
    title: `Pensión de Invalidez (${origen.toUpperCase()})`,
    total: Math.round(mesada),
    summary: `PCL ${pcl}% con tasa de reemplazo de ${tasaReemplazo.toFixed(1)}%. Mesada estimada en $${Math.round(mesada).toLocaleString("es-CO")} COP.`,
    breakdown: [
      { label: "Porcentaje de PCL Dictaminado", value: `${pcl}%` },
      { label: "Origen de la Invalidez", value: origen === "comun" ? "Origen Común (Fondo de Pensiones)" : "Origen Laboral (ARL)" },
      { label: "Semanas Acreditadas", value: `${semanasUltimos3Anios} Semanas (Requerido: 50 en últimos 3 años)` },
      { label: "Ingreso Base de Liquidación (IBL)", value: ibl },
      { label: "Regla y Tasa de Reemplazo", value: `${tasaReemplazo.toFixed(1)}% (${reglaAplicada})` },
      { label: "Garantía de Pensión Mínima (2026)", value: `$${smlmv2026.toLocaleString("es-CO")} COP` },
      { label: "Monto Final Mesada Pensional", value: Math.round(mesada) }
    ],
    normativa: [
      "Ley 100 de 1993 Artículos 38, 39 y 40",
      "Ley 860 de 2003 (Requisitos de semanas y PCL)",
      "Ley 776 de 2002 (Pensión de Invalidez de Origen Laboral ARL)",
      "Corte Constitucional Sentencia C-428/2009 (Inexequibilidad del requisito de fidelidad)"
    ]
  };
}

// 13. Retroactivo Pensional Detallado
export function calcularRetroactivoPensional(
  diferenciaMesada: number,
  mesesRetroactivo: number,
  modalidad: "ipc" | "mora" | "mixto" = "ipc",
  incluyeMesadasAdicionales: boolean = true
): CalculationResult {
  // Ajuste por mesadas adicionales (Junio y Diciembre)
  const anios = mesesRetroactivo / 12;
  const mesadasAdicionalesEstimadas = incluyeMesadasAdicionales ? Math.floor(anios * 2) : 0;
  const totalMesadasImpagas = mesesRetroactivo + mesadasAdicionalesEstimadas;

  const capitalSinActualizar = diferenciaMesada * totalMesadasImpagas;

  let factorActualizacion = 1.0;
  let etiquetaActualizacion = "";

  if (modalidad === "ipc") {
    factorActualizacion = 1 + (0.054 * anios); // Inflación promedio acumulada
    etiquetaActualizacion = "Indexación acumulada por IPC DANE";
  } else if (modalidad === "mora") {
    factorActualizacion = 1 + (0.12 * anios); // Intereses moratorios Art. 141 Ley 100
    etiquetaActualizacion = "Intereses de Mora (Art. 141 Ley 100 - Tasa Usura)";
  } else {
    factorActualizacion = 1 + (0.16 * anios);
    etiquetaActualizacion = "Modalidad Mixta (Indexación IPC + Intereses moratorios)";
  }

  const totalCalculado = capitalSinActualizar * factorActualizacion;
  const valorIncremento = totalCalculado - capitalSinActualizar;

  return {
    title: `Liquidación de Retroactivo Pensional (${modalidad.toUpperCase()})`,
    total: Math.round(totalCalculado),
    summary: `Reclamación por ${mesesRetroactivo} meses de mesadas impagas/diferenciales más ${mesadasAdicionalesEstimadas} mesadas adicionales.`,
    breakdown: [
      { label: "Diferencia Mensual por Mesada", value: diferenciaMesada },
      { label: "Meses Transcurridos de Retroactivo", value: mesesRetroactivo },
      { label: "Mesadas Adicionales (Junio/Diciembre)", value: mesadasAdicionalesEstimadas },
      { label: "Total Mesadas Impagas en Período", value: totalMesadasImpagas },
      { label: "Subtotal Capital Retroactivo Sin Actualizar", value: Math.round(capitalSinActualizar) },
      { label: "Modalidad de Actualización", value: etiquetaActualizacion },
      { label: "Valor Adicional por IPC / Mora", value: Math.round(valorIncremento) },
      { label: "Total Retroactivo Pensional a Reclamar", value: Math.round(totalCalculado) }
    ],
    normativa: [
      "Ley 100 de 1993 Artículo 141 (Intereses de mora en el reconocimiento pensional)",
      "Jurisprudencia Sala de Casación Laboral CSJ sobre indexación de la primera mesada y retroactivos",
      "Constitución Política Art. 53 (Garantía del pago oportuno y reajuste periódico de pensiones)"
    ]
  };
}

// 14. Vencimiento de Términos Penales (Art. 317 CPP)
export function calcularVencimientoTerminos(
  evento: "imputacion_acusacion" | "acusacion_juicio" | "juicio_fallo",
  diasTranscurridos: number,
  duplicado: boolean = false
): CalculationResult {
  let limiteDias = 60;
  let causalText = "Art. 175 / Art. 317 Causal 4 (Imputación a Acusación)";

  if (evento === "acusacion_juicio") {
    limiteDias = 120;
    causalText = "Art. 317 Causal 5 (Acusación a Juicio Oral)";
  } else if (evento === "juicio_fallo") {
    limiteDias = 150;
    causalText = "Art. 317 Causal 6 (Juicio Oral a Sentido del Fallo)";
  }

  if (duplicado) {
    limiteDias *= 2;
    causalText += " [Términos Duplicados: 3+ Imputados / Juzgado Especializado / GAO]";
  }

  const vencido = diasTranscurridos > limiteDias;
  const diasExceso = Math.max(0, diasTranscurridos - limiteDias);

  return {
    title: "Cómputo Vencimiento de Términos (Art. 317 CPP)",
    total: diasExceso,
    summary: vencido
      ? `¡TÉRMINOS VENCIDOS! Se superó el límite por ${diasExceso} días. Procede solicitud de libertad inmediata.`
      : `Términos vigentes. Quedan ${limiteDias - diasTranscurridos} días antes del vencimiento.`,
    breakdown: [
      { label: "Causal y Etapa Procesal", value: causalText },
      { label: "Límite Máximo Permitido (Días)", value: limiteDias },
      { label: "Días Transcurridos en la Etapa", value: diasTranscurridos },
      { label: "Estado Procesal", value: vencido ? "VENCIDO - Procede Libertad" : "DENTRO DEL TÉRMINO" },
      { label: "Días de Exceso en Libertad", value: diasExceso }
    ],
    normativa: [
      "Ley 906 de 2004 (Código de Procedimiento Penal) Artículos 175, 292, 294 y 317",
      "Ley 1786 de 2016 y Ley 1908 de 2018 (Prorrogas y términos en juzgados especializados)"
    ]
  };
}

// 15. Dosificación Punitiva (Ley 599/2000 Art. 61)
export function calcularDosificacionPunitiva(
  penaMinimaMeses: number,
  penaMaximaMeses: number,
  cuartoSeleccionado: "minimo" | "medio_inferior" | "medio_superior" | "maximo",
  porcentajeAllanamiento: number = 0
): CalculationResult {
  const ambitoPunitivo = penaMaximaMeses - penaMinimaMeses;
  const tamanoCuarto = ambitoPunitivo / 4;

  let minCuarto = penaMinimaMeses;
  let maxCuarto = penaMinimaMeses + tamanoCuarto;

  if (cuartoSeleccionado === "medio_inferior") {
    minCuarto = penaMinimaMeses + tamanoCuarto;
    maxCuarto = penaMinimaMeses + 2 * tamanoCuarto;
  } else if (cuartoSeleccionado === "medio_superior") {
    minCuarto = penaMinimaMeses + 2 * tamanoCuarto;
    maxCuarto = penaMinimaMeses + 3 * tamanoCuarto;
  } else if (cuartoSeleccionado === "maximo") {
    minCuarto = penaMinimaMeses + 3 * tamanoCuarto;
    maxCuarto = penaMaximaMeses;
  }

  const penaIndividualizada = minCuarto;
  const factorRebaja = 1 - (porcentajeAllanamiento / 100);
  const penaDefinitiva = penaIndividualizada * factorRebaja;

  return {
    title: "Dosificación Punitiva (Sistema de Cuartos CP)",
    total: parseFloat(penaDefinitiva.toFixed(2)),
    summary: `Pena individualizada en el ${cuartoSeleccionado.toUpperCase().replace("_", " ")} con rebaja del ${porcentajeAllanamiento}% por preacuerdo.`,
    breakdown: [
      { label: "Marco Punitivo Legal", value: `${penaMinimaMeses} a ${penaMaximaMeses} Meses` },
      { label: "Ámbito Punitivo Total", value: `${ambitoPunitivo} Meses` },
      { label: "Extensión de Cada Cuarto", value: `${tamanoCuarto.toFixed(2)} Meses` },
      { label: "Rango del Cuarto Seleccionado", value: `${minCuarto.toFixed(2)} a ${maxCuarto.toFixed(2)} Meses` },
      { label: "Pena Individualizada Base", value: `${penaIndividualizada.toFixed(2)} Meses` },
      { label: "Rebaja Allanamiento / Preacuerdo", value: `${porcentajeAllanamiento}%` },
      { label: "Pena Definitiva Imponible", value: `${penaDefinitiva.toFixed(2)} Meses (${(penaDefinitiva / 12).toFixed(2)} Años)` }
    ],
    normativa: [
      "Código Penal Colombiano (Ley 599 de 2000) Artículos 60 y 61",
      "Ley 906 de 2004 Artículos 350 a 354 (Preacuerdos y Allanamiento a Cargos)"
    ]
  };
}

// 12. Tabla de rentistas Superfinanciera (Resolución 1555 de 2010 / Res. 0572 de 2013)
// Fuente oficial utilizada por la Sala de Casación Laboral de la Corte Suprema de Justicia
export const TABLA_EV_RENTISTAS_CSJ: Record<"H" | "M", Record<number, number>> = {
  H: {
    15: 64.8, 16: 63.9, 17: 62.9, 18: 61.9, 19: 60.9, 20: 60.0, 21: 59.0, 22: 58.0, 23: 57.1, 24: 56.1,
    25: 55.1, 26: 54.2, 27: 53.2, 28: 52.3, 29: 51.3, 30: 50.3, 31: 49.4, 32: 48.4, 33: 47.5, 34: 46.5,
    35: 45.6, 36: 44.6, 37: 43.7, 38: 42.7, 39: 41.8, 40: 40.8, 41: 39.9, 42: 39.0, 43: 38.0, 44: 37.1,
    45: 36.2, 46: 35.3, 47: 34.4, 48: 33.4, 49: 32.5, 50: 31.6, 51: 30.7, 52: 29.9, 53: 29.0, 54: 28.1,
    55: 27.2, 56: 26.4, 57: 25.5, 58: 24.6, 59: 23.8, 60: 23.0, 61: 22.1, 62: 21.3, 63: 20.5, 64: 19.7,
    65: 19.0, 66: 18.2, 67: 17.4, 68: 16.7, 69: 16.0, 70: 15.3, 71: 14.6, 72: 14.0, 73: 13.3, 74: 12.7,
    75: 12.1, 76: 11.5, 77: 10.9, 78: 10.3, 79: 9.7, 80: 9.2, 81: 8.6, 82: 8.1, 83: 7.6, 84: 7.1,
    85: 6.6, 86: 6.2, 87: 5.7, 88: 5.3, 89: 4.9, 90: 4.5, 91: 4.2, 92: 3.8, 93: 3.5, 94: 3.2,
    95: 3.0, 96: 2.7, 97: 2.5, 98: 2.3, 99: 2.1, 100: 1.9, 105: 1.6, 110: 0.5
  },
  M: {
    15: 70.0, 16: 69.1, 17: 68.1, 18: 67.1, 19: 66.1, 20: 65.1, 21: 64.2, 22: 63.2, 23: 62.2, 24: 61.2,
    25: 60.2, 26: 59.3, 27: 58.3, 28: 57.3, 29: 56.3, 30: 55.4, 31: 54.4, 32: 53.4, 33: 52.4, 34: 51.5,
    35: 50.5, 36: 49.5, 37: 48.6, 38: 47.6, 39: 46.6, 40: 45.7, 41: 44.7, 42: 43.7, 43: 42.8, 44: 41.8,
    45: 40.9, 46: 39.9, 47: 39.0, 48: 38.0, 49: 37.1, 50: 36.2, 51: 35.2, 52: 34.3, 53: 33.4, 54: 32.5,
    55: 31.6, 56: 30.6, 57: 29.7, 58: 28.8, 59: 27.9, 60: 27.0, 61: 26.2, 62: 25.3, 63: 24.4, 64: 23.5,
    65: 22.7, 66: 21.8, 67: 21.0, 68: 20.2, 69: 19.4, 70: 18.6, 71: 17.8, 72: 17.0, 73: 16.2, 74: 15.5,
    75: 14.7, 76: 14.0, 77: 13.3, 78: 12.6, 79: 11.9, 80: 11.3, 81: 10.6, 82: 10.0, 83: 9.4, 84: 8.9,
    85: 8.3, 86: 7.8, 87: 7.3, 88: 6.8, 89: 6.3, 90: 5.8, 91: 5.4, 92: 5.0, 93: 4.6, 94: 4.2,
    95: 3.9, 96: 3.5, 97: 3.2, 98: 3.0, 99: 2.7, 100: 2.5, 105: 1.6, 110: 0.5
  }
};

export function obtenerEVRentistas(edad: number, sexo: "H" | "M"): number {
  const tabla = TABLA_EV_RENTISTAS_CSJ[sexo];
  if (!tabla) return 21.3;
  if (tabla[edad] !== undefined) return tabla[edad];

  const edades = Object.keys(tabla).map(Number).sort((a, b) => a - b);
  if (edad <= edades[0]) return tabla[edades[0]];
  if (edad >= edades[edades.length - 1]) return tabla[edades[edades.length - 1]];

  for (let i = 0; i < edades.length - 1; i++) {
    const a0 = edades[i];
    const a1 = edades[i + 1];
    if (edad >= a0 && edad <= a1) {
      const v0 = tabla[a0];
      const v1 = tabla[a1];
      return Number((v0 + (v1 - v0) * ((edad - a0) / (a1 - a0))).toFixed(1));
    }
  }
  return 21.3;
}

export function calcularPerjuiciosTrasladoPensional(
  semanas: number,
  edadTraslado: number,
  edadExigida: number,
  mesadaRPM: number,
  mesadaRAIS: number,
  expectativaVida: number,
  mesadasAnio: number = 13
): CalculationResult {
  const base = 0.5;
  const compSemanas = Math.pow(Math.max(0, semanas) / 1300, 0.5);
  const compEdad = Math.pow(2, -(Math.abs(edadTraslado - edadExigida) / 100));
  const pp = base * compSemanas * compEdad;
  const diffMensual = Math.max(0, mesadaRPM - mesadaRAIS);
  const pagosEsperados = expectativaVida * mesadasAnio;
  const indemnizacionTotal = Math.trunc(pp * diffMensual * pagosEsperados);

  return {
    title: "Indemnización por Ineficacia del Traslado Pensional (Pérdida de Oportunidad)",
    total: indemnizacionTotal,
    summary: `Indemnización estimada en $${indemnizacionTotal.toLocaleString("es-CO")} con Probabilidad Pensional (PP) de ${(pp * 100).toFixed(2)}% y brecha de mesada de $${diffMensual.toLocaleString("es-CO")}.`,
    breakdown: [
      { label: "Semanas Cotizadas al Traslado (S)", value: semanas },
      { label: "Componente Semanas (S / 1300)^0.5", value: compSemanas.toFixed(4) },
      { label: "Componente Edad 2^(-|EP - ER| / 100)", value: compEdad.toFixed(4) },
      { label: "Probabilidad Pensional (PP)", value: `${pp.toFixed(4)} (${(pp * 100).toFixed(2)}%)` },
      { label: "Mesada Estimada en RPM", value: `$${mesadaRPM.toLocaleString("es-CO")}` },
      { label: "Mesada Estimada en RAIS", value: `$${mesadaRAIS.toLocaleString("es-CO")}` },
      { label: "Brecha Económica Mensual (RPM - RAIS)", value: `$${diffMensual.toLocaleString("es-CO")}` },
      { label: "Expectativa de Vida (Años)", value: `${expectativaVida} años` },
      { label: "Pagos Futuros Esperados (EV × 13 mesadas)", value: `${pagosEsperados.toFixed(1)} mesadas` },
      { label: "Indemnización Total Condenatoria", value: `$${indemnizacionTotal.toLocaleString("es-CO")}` }
    ],
    normativa: [
      "Corte Suprema de Justicia — Sala de Casación Laboral (Relatoría)",
      "Fórmula de Pérdida de Oportunidad Pensional: PP × (MesadaRPM − MesadaRAIS) × (EVaños × 13)",
      "Superintendencia Financiera de Colombia — Tabla de Rentistas (Resolución 1555 de 2010)"
    ]
  };
}
